import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Bell, X } from 'lucide-react';

/**
 * NotificationPrompt - Prompts mobile PWA users to enable notifications
 * Shows once per session if notifications aren't already granted
 */
export default function NotificationPrompt() {
  const [show, setShow] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    // Only show on PWA and if notifications are supported
    const isPWA = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    const notificationsSupported = 'Notification' in window;
    const notDismissed = !sessionStorage.getItem('notification-prompt-dismissed');
    const notGranted = notificationsSupported && Notification.permission === 'default';

    // Show prompt if: PWA + notifications supported + not dismissed + not already granted/denied
    if (isPWA && notificationsSupported && notDismissed && notGranted) {
      // Delay showing to not interrupt initial experience
      const timer = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleEnable = async () => {
    setRequesting(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        // Show a test notification
        new Notification('PocketFiler', {
          body: 'Notifications enabled! You\'ll receive updates on deals and messages.',
          icon: '/icons/icon-192x192.png'
        });
      }
    } catch (err) {
      console.error('Notification permission error:', err);
    } finally {
      setRequesting(false);
      setShow(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('notification-prompt-dismissed', 'true');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:w-80 animate-slide-up">
      <Card className="border-primary/20 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-primary/10 text-primary">
              <Bell className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">Enable Notifications</p>
              <p className="text-xs text-muted-foreground mt-1">
                Get notified about new messages, deal updates, and contact activity.
              </p>
              <div className="flex gap-2 mt-3">
                <Button 
                  size="sm" 
                  onClick={handleEnable}
                  disabled={requesting}
                  className="flex-1"
                >
                  {requesting ? 'Enabling...' : 'Enable'}
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={handleDismiss}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
